import { create } from "zustand";

/**
 * Interface defining the structure of job ownership state and actions
 */
interface JobOwnershipState {
  // State - hashmap with jobId as key and ownership status as boolean value
  jobOwnership: Record<string, boolean>;

  // Actions
  setJobOwnership: (jobId: string, isOwned: boolean) => void;
  setMultipleJobOwnership: (ownershipMap: Record<string, boolean>) => void;
  removeJobOwnership: (jobId: string) => void;
  clearJobOwnership: () => void;
  isJobOwned: (jobId: string) => boolean | undefined;
  getOwnedJobs: () => string[];
  getNotOwnedJobs: () => string[];
}

/**
 * Initial state for the job ownership store
 */
const initialState = {
  jobOwnership: {}
};

/**
 * Zustand store for managing job ownership status
 * Tracks which jobs are owned by the current logged-in user
 */
const useJobOwnershipStore = create<JobOwnershipState>((set, get) => ({
  ...initialState,

  /**
   * Set ownership status for a single job
   * @param jobId - The ID of the job
   * @param isOwned - Whether the job is owned by the current user
   */
  setJobOwnership: (jobId: string, isOwned: boolean) =>
    set(state => ({
      jobOwnership: {
        ...state.jobOwnership,
        [jobId]: isOwned
      }
    })),

  /**
   * Set ownership status for multiple jobs at once
   * @param ownershipMap - Object with jobId as keys and ownership status as values
   */
  setMultipleJobOwnership: (ownershipMap: Record<string, boolean>) =>
    set(state => ({
      jobOwnership: {
        ...state.jobOwnership,
        ...ownershipMap
      }
    })),

  /**
   * Remove a job from the ownership tracking
   * @param jobId - The ID of the job to remove
   */
  removeJobOwnership: (jobId: string) =>
    set(state => {
      const { [jobId]: removed, ...remaining } = state.jobOwnership;
      return { jobOwnership: remaining };
    }),

  /**
   * Clear all job ownership data
   */
  clearJobOwnership: () => set(initialState),

  /**
   * Check if a specific job is owned by the current user
   * @param jobId - The ID of the job to check
   * @returns boolean | undefined - true if owned, false if not owned, undefined if not tracked
   */
  isJobOwned: (jobId: string) => {
    const state = get();
    return state.jobOwnership[jobId];
  },

  /**
   * Get array of job IDs that are owned by the current user
   * @returns string[] - Array of owned job IDs
   */
  getOwnedJobs: () => {
    const state = get();
    return Object.entries(state.jobOwnership)
      .filter(([, isOwned]) => isOwned)
      .map(([jobId]) => jobId);
  },

  /**
   * Get array of job IDs that are not owned by the current user
   * @returns string[] - Array of not owned job IDs
   */
  getNotOwnedJobs: () => {
    const state = get();
    return Object.entries(state.jobOwnership)
      .filter(([, isOwned]) => !isOwned)
      .map(([jobId]) => jobId);
  }
}));

export default useJobOwnershipStore;

